import { useQuery } from '@tanstack/react-query';
import { marketing } from '@/lib/supabase/marketing';

export interface ListingHealth {
  outlet_id: string;
  total_reviews: number | null;
  avg_star_rating: number | null;
  views_total: number | null;
  views_maps: number | null;
  views_search: number | null;
  actions_total: number | null;
  actions_phone: number | null;
  actions_website: number | null;
  actions_directions: number | null;
  actions_menu_clicks: number | null;
  actions_food_orders: number | null;
  unreplied_count: number | null;
  avg_reply_minutes: number | null;
  synced_at: string;
}

export function useListingHealth() {
  return useQuery({
    queryKey: ['gmb-listing-health'],
    queryFn: async (): Promise<ListingHealth[]> => {
      const { data, error } = await marketing.from('gmb_listing_health').select('*');
      if (error) throw error;
      return (data ?? []) as unknown as ListingHealth[];
    },
    staleTime: 5 * 60_000,
  });
}

export interface GoogleReview {
  review_id: string;
  outlet_id: string;
  star_rating: number;
  reviewer_name: string | null;
  comment: string | null;
  reply_comment: string | null;
  created_at: string;
  reply_at: string | null;
}

interface UseReviewsArgs {
  outletId?: string | 'all';
  /** Only return reviews that haven't been replied to. */
  onlyUnreplied?: boolean;
  /** Only return reviews with star_rating <= 2. */
  onlyNegative?: boolean;
  limit?: number;
}

export function useGoogleReviews({
  outletId,
  onlyUnreplied,
  onlyNegative,
  limit = 50,
}: UseReviewsArgs = {}) {
  return useQuery({
    queryKey: ['gmb-reviews', outletId ?? 'all', !!onlyUnreplied, !!onlyNegative, limit],
    queryFn: async (): Promise<GoogleReview[]> => {
      let q = marketing
        .from('gmb_reviews')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(limit);
      if (outletId && outletId !== 'all') q = q.eq('outlet_id', outletId);
      if (onlyUnreplied) q = q.or('reply_comment.is.null,reply_comment.eq.');
      if (onlyNegative) q = q.lte('star_rating', 2);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as unknown as GoogleReview[];
    },
    staleTime: 60_000,
  });
}
